<script setup lang="ts">
import type { Instant } from '@taler/domain'
import { formatInstantDate } from '@taler/domain'
import { ActionButton, StatusTag } from '@taler/ui'
import { reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import RoleSelect from '@/features/membership/components/RoleSelect.vue'
import type { Invitation, LedgerRole } from '../internal/types.ts'

const props = defineProps<{
  invitations: readonly Invitation[]
  isInviting: boolean
  inviteError: Error | null
  revokingId: string | undefined
  revokeError: Error | null
  locale: 'en' | 'hu'
}>()

const emit = defineEmits<{
  invite: [input: { subject: string; role: LedgerRole }]
  revoke: [invitationId: string]
}>()

const { t } = useI18n()

const form = reactive<{ subject: string; role: LedgerRole }>({ subject: '', role: 'OBSERVER' })
const confirmingId = ref<string | undefined>(undefined)

function submit(): void {
  if (form.subject.trim() === '') return
  emit('invite', { subject: form.subject.trim(), role: form.role })
}

watch(
  () => props.isInviting,
  (inviting, wasInviting) => {
    if (wasInviting && !inviting && props.inviteError === null) form.subject = ''
  },
)

function expiry(value: Instant): string {
  return formatInstantDate(value, props.locale)
}

function statusKey(status: string): string {
  return status === 'PENDING' || status === 'ACCEPTED' || status === 'REVOKED'
    ? `membership.status${status.charAt(0)}${status.slice(1).toLowerCase()}`
    : status
}
</script>

<template>
  <section class="flex flex-col gap-[var(--space-block)]">
    <h2 class="text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]">
      {{ t('membership.invitationsTitle') }}
    </h2>

    <p v-if="props.invitations.length === 0" class="text-[color:var(--color-text-muted)]">
      {{ t('membership.invitationsEmpty') }}
    </p>

    <table v-else class="w-full border-collapse bg-[color:var(--color-surface)]">
      <thead>
        <tr class="table-head">
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            {{ t('membership.invitedSubject') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            {{ t('membership.role') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            {{ t('membership.status') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            {{ t('membership.expiresAt') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            <span class="sr-only">{{ t('membership.revoke') }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="invitation in props.invitations"
          :key="invitation.id"
          class="border-b border-[color:var(--color-border)]"
        >
          <td
            class="px-[var(--space-cell)] py-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
          >
            {{ invitation.invitedSubject }}
          </td>
          <td class="px-[var(--space-cell)] py-[var(--space-cell)]">
            <span
              class="whitespace-nowrap rounded-full border border-[color:var(--color-border)] px-[var(--space-inline)] py-[var(--space-hair)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
            >
              {{ t(`membership.roleName.${invitation.role}`) }}
            </span>
          </td>
          <td
            class="px-[var(--space-cell)] py-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
          >
            {{ t(statusKey(invitation.status)) }}
          </td>
          <td
            class="px-[var(--space-cell)] py-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
          >
            {{ expiry(invitation.expiresAt) }}
          </td>
          <td class="px-[var(--space-cell)] py-[var(--space-cell)] text-right">
            <template v-if="invitation.status === 'PENDING'">
              <div
                v-if="confirmingId === invitation.id"
                class="flex items-center justify-end gap-[var(--space-inline)]"
              >
                <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
                  {{ t('membership.revokeConfirmBody') }}
                </span>
                <button
                  type="button"
                  :disabled="props.revokingId === invitation.id"
                  class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] bg-[color:var(--color-danger)] px-3 py-1 text-[length:var(--text-step-2)] text-[color:var(--color-text-inverse)] disabled:opacity-60"
                  @click="emit('revoke', invitation.id)"
                >
                  {{ t('membership.revokeConfirmAction') }}
                </button>
                <button
                  type="button"
                  class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-1 text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
                  @click="confirmingId = undefined"
                >
                  {{ t('membership.revokeCancel') }}
                </button>
              </div>
              <button
                v-else
                type="button"
                class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-1 text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
                @click="confirmingId = invitation.id"
              >
                {{ t('membership.revoke') }}
              </button>
            </template>
          </td>
        </tr>
      </tbody>
    </table>

    <StatusTag v-if="props.revokeError" tone="danger" role="alert">
      {{ t('membership.revokeError') }}
    </StatusTag>

    <form
      class="flex flex-col gap-[var(--space-group)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-[var(--space-block)] bg-[color:var(--color-surface)]"
      @submit.prevent="submit"
    >
      <div>
        <h3 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
          {{ t('membership.inviteTitle') }}
        </h3>
        <p class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
          {{ t('membership.inviteBody') }}
        </p>
      </div>

      <div class="flex items-end gap-[var(--space-group)]">
        <div class="flex flex-1 flex-col gap-[var(--space-tight)]">
          <label
            for="invite-subject"
            class="text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
          >
            {{ t('membership.invitedSubject') }}
          </label>
          <input
            id="invite-subject"
            v-model="form.subject"
            type="email"
            required
            class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
          />
        </div>
        <div class="w-56">
          <label
            for="invite-role"
            class="text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
          >
            {{ t('membership.role') }}
          </label>
          <RoleSelect id="invite-role" v-model="form.role" />
        </div>
        <ActionButton
          type="submit"
          :disabled="props.isInviting"
          class="shrink-0 text-[length:var(--text-step-3)]"
        >
          {{ t('membership.inviteAction') }}
        </ActionButton>
      </div>

      <StatusTag v-if="props.inviteError" tone="danger" role="alert">
        {{ t('membership.inviteError') }}
      </StatusTag>
    </form>
  </section>
</template>
